import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import StyleModal from './components/StyleModal';
import { useToast } from '../../components/ui/Toast';

const WritingStylesTemplates = () => {
  const { userProfile, updateProfile } = useAuth();
  const toast = useToast();
  const [customStyles, setCustomStyles] = useState([]);
  const [modalStyle, setModalStyle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const predefinedStyles = [
    {
      id: 'mentor',
      name: 'The Mentor',
      description: 'Educational and guiding tone that helps others learn',
      icon: 'GraduationCap',
      color: '#10B981',
      tone: 'Clear, educational, supportive',
      technicalDepth: 'Medium',
      engagement: 'Questions, teaching moments',
      usageCount: 42,
      sample: 'Let me walk you through a React optimization technique I recently discovered. When implementing code splitting with React.lazy(), you can reduce bundle sizes significantly. Here\'s why this matters and how to implement it effectively...'
    },
    {
      id: 'deep-dive',
      name: 'Deep-Dive Dev',
      description: 'Comprehensive technical analysis with detailed explanations',
      icon: 'Code2',
      color: '#A78BFA',
      tone: 'Technical, thorough, analytical',
      technicalDepth: 'High',
      engagement: 'Technical discussions, debates',
      usageCount: 28,
      sample: 'Deep dive into React bundle optimization: Implementing dynamic imports with React.lazy() yielded a 40% reduction in initial bundle size. The mechanism involves webpack code splitting at the component level, creating separate chunks loaded on-demand...'
    },
    {
      id: 'minimalist',
      name: 'The Minimalist',
      description: 'Concise and impactful — gets straight to the point',
      icon: 'Minus',
      color: '#F59E0B',
      tone: 'Brief, direct, impactful',
      technicalDepth: 'Low to Medium',
      engagement: 'Quick insights, actionable tips',
      usageCount: 35,
      sample: 'React.lazy() + code splitting = 40% smaller bundles. Dynamic imports are the way. Ship less, load faster.'
    }
  ];

  useEffect(() => {
    const customStylesJSON = localStorage.getItem('custom_writing_styles');
    if (customStylesJSON) {
      try { setCustomStyles(JSON.parse(customStylesJSON)); } catch { /* ignore */ }
    }
  }, []);

  const handleSetDefault = async (styleId) => {
    try {
      await updateProfile({ default_writing_style: styleId });
      toast.success('Default writing style updated!');
    } catch {
      toast.error('Failed to update default style.');
    }
  };

  const handleOpenStyle = (style) => {
    setShowModal(false);
    requestAnimationFrame(() => {
      setModalStyle(style);
      setIsEditing(false);
      setShowModal(true);
    });
  };

  const handleCreateNew = () => {
    // Close any open modal first to avoid stale content flash
    setShowModal(false);
    requestAnimationFrame(() => {
      setModalStyle({
        id: `custom-${Date.now()}`,
        name: '',
        description: '',
        icon: 'Sparkles',
        color: '#A78BFA',
        tone: '',
        technicalDepth: 'Medium',
        engagement: '',
        isCustom: true
      });
      setIsEditing(true);
      setShowModal(true);
    });
  };

  const handleSaveStyle = (styleData) => {
    setCustomStyles(prev => {
      const existing = prev.find(s => s.id === styleData.id);
      const updated = existing ? prev.map(s => s.id === styleData.id ? styleData : s) : [...prev, styleData];
      localStorage.setItem('custom_writing_styles', JSON.stringify(updated));
      return updated;
    });
    toast.success('Style saved!');
  };

  const handleDeleteStyle = (styleId) => {
    if (!window.confirm('Delete this custom style?')) return;
    setCustomStyles(prev => {
      const updated = prev.filter(s => s.id !== styleId);
      localStorage.setItem('custom_writing_styles', JSON.stringify(updated));
      return updated;
    });
    toast.success('Style deleted.');
  };

  const allStyles = [...predefinedStyles, ...customStyles];
  const defaultStyleId = userProfile?.default_writing_style;

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">Writing Styles</h1>
            <p className="text-sm text-muted-foreground">
              Define how your posts sound — choose a preset or create your own
            </p>
          </div>

          {/* Predefined Styles — centered chip cards */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {predefinedStyles.map((style) => {
              const isDefault = defaultStyleId === style.id;
              return (
                <button
                  key={style.id}
                  onClick={() => handleOpenStyle(style)}
                  className="group flex items-center gap-3 px-5 py-3.5 bg-card border border-border hover:border-border rounded-xl transition-all duration-200 hover:shadow-lg"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${style.color}15` }}>
                    <Icon name={style.icon} size={16} color={style.color} />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground group-hover:text-foreground transition-colors">{style.name}</span>
                      {isDefault && (
                        <span className="px-1.5 py-0.5 bg-violet-500/15 text-violet-400 text-[9px] font-medium rounded">Default</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{style.description}</p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-muted-foreground group-hover:text-muted-foreground transition-colors ml-1" />
                </button>
              );
            })}
          </div>

          {/* Custom Styles Section */}
          {customStyles.length > 0 && (
            <div className="mb-8">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center mb-4">Your Custom Styles</p>
              <div className="flex flex-wrap justify-center gap-3">
                {customStyles.map((style) => {
                  const isDefault = defaultStyleId === style.id;
                  return (
                    <button
                      key={style.id}
                      onClick={() => handleOpenStyle(style)}
                      className="group flex items-center gap-3 px-4 py-3 bg-card border border-border hover:border-border rounded-xl transition-all duration-200"
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${style.color || '#A78BFA'}15` }}>
                        <Icon name={style.icon || 'Sparkles'} size={14} color={style.color || '#A78BFA'} />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{style.name}</span>
                          {isDefault && (
                            <span className="px-1.5 py-0.5 bg-violet-500/15 text-violet-400 text-[9px] font-medium rounded">Default</span>
                          )}
                        </div>
                      </div>
                      <Icon name="ChevronRight" size={14} className="text-muted-foreground group-hover:text-muted-foreground transition-colors" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create New Style Button */}
          <div className="flex justify-center">
            <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-5 py-3 bg-card border border-dashed border-border hover:border-violet-500/50 hover:bg-muted rounded-xl transition-all duration-200 text-sm text-muted-foreground hover:text-foreground"
            >
              <Icon name="Plus" size={16} />
              <span>Create New Style</span>
            </button>
          </div>
        </div>
      </main>

      {/* Style Modal */}
      <StyleModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setIsEditing(false); }}
        style={modalStyle}
        isEditing={isEditing}
        onSave={handleSaveStyle}
        onSetDefault={handleSetDefault}
        onDelete={handleDeleteStyle}
        isDefault={defaultStyleId === modalStyle?.id}
      />
    </div>
  );
};

export default WritingStylesTemplates;
